'use client';
import { alpha, Box, Chip, Paper, Typography, useTheme } from '@mui/material';
import { ChartsTooltipContainer, useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import { rainbowSurgePalette } from '@mui/x-charts/colorPalettes';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TestResult } from '@/types';
import { getTrendData } from '@/utils/client/test-feedback';

const marginLeft = 48;
const marginRight = 48;
const marginTop = 8;
const marginBottom = 36;
const chartHeight = 200;
const pastRunOpacity = 0.45;

function createTrendTooltip(dateByLabel: Record<string, string>) {
  return function TrendTooltip() {
    const axesTooltipData = useAxesTooltip();
    if (!axesTooltipData || axesTooltipData.length === 0) return null;

    return (
      <ChartsTooltipContainer trigger="axis">
        <Paper variant="outlined" sx={{ px: 1.5, py: 1 }}>
          {axesTooltipData.map(({ axisId, axisValue, axisFormattedValue, seriesItems }) => {
            const date = dateByLabel[String(axisValue)];
            const item = seriesItems.find((seriesItem) => seriesItem.formattedValue != null);

            return (
              <Box key={axisId}>
                <Typography variant="body2">
                  <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                    {axisFormattedValue}
                  </Typography>
                  {date ? `: ${date}` : null}
                </Typography>
                {item && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: item.color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.formattedValue}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Paper>
      </ChartsTooltipContainer>
    );
  };
}

export default function TestProgressTrend({
  testResults,
  delta,
}: {
  testResults: TestResult[];
  delta?: number;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      setChartWidth(container.getBoundingClientRect().width);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  const { labels, values, currentValues, dateByLabel } = useMemo(() => {
    const trendData = getTrendData(testResults);
    const labels = trendData.map((item) => t('test.results.runTab', { run: item.run }));
    const dateByLabel = Object.fromEntries(
      trendData.map((item, index) => [labels[index], dayjs(item.createdAt).format('DD.MM.YY')]),
    );
    const values = trendData.map((item) => item.percentage);
    const latestIndex = values.length - 1;
    const currentValues = values.map((value, index) => (index === latestIndex ? value : null));

    return { labels, values, currentValues, dateByLabel };
  }, [testResults, t]);

  const TrendTooltip = useMemo(() => createTrendTooltip(dateByLabel), [dateByLabel]);

  if (testResults.length < 2) return null;

  const currentColor = rainbowSurgePalette(theme.palette.mode)[0];
  const pastColor = alpha(currentColor, pastRunOpacity);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.375 }}>
        <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>
          {t('test.insight.trend.title')}
        </Typography>
        {delta !== undefined && delta !== 0 && (
          <Chip
            size="small"
            label={
              delta > 0
                ? t('test.insight.trend.deltaUp', { delta: `+${delta}` })
                : t('test.insight.trend.deltaDown', { delta })
            }
            color={delta > 0 ? 'success' : 'warning'}
            variant="outlined"
          />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('test.insight.trend.subtitle')}
      </Typography>
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          mx: 'auto',
          mb: -2,
        }}>
        {chartWidth > 0 && (
          <LineChart
            width={chartWidth}
            height={chartHeight}
            hideLegend
            xAxis={[{ scaleType: 'point', data: labels }]}
            yAxis={[{ min: 0, max: 100 }]}
            series={[
              {
                id: 'trend',
                data: values,
                showMark: true,
                curve: 'linear',
                color: pastColor,
                valueFormatter: (value) => (value == null ? null : `${value}%`),
              },
              {
                id: 'trend-current',
                data: currentValues,
                showMark: true,
                curve: 'linear',
                color: currentColor,
                valueFormatter: () => null,
              },
            ]}
            margin={{ left: marginLeft, right: marginRight, top: marginTop, bottom: marginBottom }}
            slots={{ tooltip: TrendTooltip }}
          />
        )}
      </Box>
    </Box>
  );
}
